import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { UnsubscribeComponent } from '../../generals/Components';
import { UserEditor, UserGroup } from '../../interfaces/interfaces';

@Component({
  selector: 'app-usereditor',
  templateUrl: './usereditor.component.html',
  styleUrls: ['./usereditor.component.scss']
})
export class UsereditorComponent extends UnsubscribeComponent implements AfterViewInit {

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  userId: number = null;
  userEditor: UserEditor = null;
  userGroups: UserGroup[] = [];
  dataLoading = false;
  loading = false;
  userEditorFormGroup: FormGroup = null;

  init(): void {
    this.initUserEditorFormGroup();
    this.loadData();
  }

  loadData(): void {
    this.dataLoading = true;
    if (this.userId === null) {
      combineLatest([
        this.apiService.getSimpleTable<UserGroup>('usergroups')
      ]).pipe(takeUntil(this.unsubscribe)).subscribe(([getUserGroupsResponse]) => {
        this.userGroups = getUserGroupsResponse.data;
      }).add(() => {
        this.dataLoading = false;
      });
    } else {
      combineLatest([
        this.apiService.getSimpleTable<UserGroup>('usergroups'),
        this.apiService.getUser(this.userId)
      ]).pipe(takeUntil(this.unsubscribe)).subscribe(([getUserGroupsResponse, getUserResponse]) => {
        this.userGroups = getUserGroupsResponse.data;
        this.userEditor = getUserResponse.userEditor;
        this.fillUserEditorForm(this.userEditor);
      }).add(() => {
        this.dataLoading = false;
      });
    }
  }

  initUserEditorFormGroup(): void {
    this.userEditorFormGroup = new FormGroup({
      username: new FormControl('', [Validators.required]),
      fullname: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      active: new FormControl(true),
      apiKey: new FormControl(null),
      apiEnabled: new FormControl(false),
      renewApiKey: new FormControl(false),
      userGroups: new FormControl([])
    });
  }

  fillUserEditorForm(userEditor: UserEditor): void {
    this.userEditorFormGroup.get('username').setValue(userEditor.username);
    this.userEditorFormGroup.get('fullname').setValue(userEditor.fullname);
    this.userEditorFormGroup.get('apiEnabled').setValue(userEditor.apiEnabled);
    this.userEditorFormGroup.get('apiKey').setValue(userEditor.apiKey);
    this.userEditorFormGroup.get('password').setValue(userEditor.password);
    this.userEditorFormGroup.get('active').setValue(userEditor.active);
    this.userEditorFormGroup.get('userGroups').setValue(this.userGroups.filter((userGroup) => {
      return userEditor.userGroups.indexOf(userGroup.id) !== -1;
    }));
    this.userEditorFormGroup.get('password').clearValidators();
    this.userEditorFormGroup.get('password').updateValueAndValidity({
      emitEvent: false
    });
  }

  save(): void {
    if (this.userEditorFormGroup.invalid) {
      return;
    }
    this.loading = true;
    const userEditorFormValue: any = this.userEditorFormGroup.value;
    if (this.userId === null) {
      this.apiService.createUser({
        active: userEditorFormValue.active,
        fullname: userEditorFormValue.fullname,
        password: userEditorFormValue.password,
        username: userEditorFormValue.username,
        userGroups: userEditorFormValue.userGroups.map(userGroup => userGroup.id),
        apiKey: userEditorFormValue.apiKey,
        apiEnabled: userEditorFormValue.apiEnabled,
        renewApiKey: userEditorFormValue.renewApiKey
      }).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.ref.close(true);
      }).add(() => {
        this.loading = false;
      });
    } else {
      this.apiService.updateUser(this.userId, {
        active: userEditorFormValue.active,
        fullname: userEditorFormValue.fullname,
        password: userEditorFormValue.password,
        username: userEditorFormValue.username,
        userGroups: userEditorFormValue.userGroups.map(userGroup => userGroup.id),
        apiKey: userEditorFormValue.apiKey,
        apiEnabled: userEditorFormValue.apiEnabled,
        renewApiKey: userEditorFormValue.renewApiKey
      }).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.ref.close(true);
      }).add(() => {
        this.loading = false;
      });
    }
  }

  close(result: boolean = false): void {
    this.ref.close(result);
  }

  constructor(
    private apiService: ApiService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig
  ) {
    super();
    this.userId = this.config.data.userId;
    this.init();
  }

  ngAfterViewInit(): void {
    this.input.nativeElement.focus();
  }
}
